<template>
    <main>
        <div class="contents">
            <div class="form">
                <h1 class="helvetica title1">Forgot password?</h1>
                <div class="body">
                    <p>Add the email with which you registered in the field below. We’ll send you a link you'll be able to use to update your password. If you don’t receive the email within a few minutes, check your spam/junk folder. If you still don’t see it, you may want to try again or contact <a href="mailto:info@eic-federation.eu">info@eic-federation.eu</a> for further assistance.</p>
                </div>
                <div class="form-container">
                    <NForm ref="loginForm" :model="formData" :rules="rules" @submit.prevent="validateForm">
                        <NFormItem label="Email" path="email" size="large">
                            <NInput v-model:value="formData.email" type="text" :input-props="{ type: 'email', autocomplete: 'email' }" placeholder="" required autofocus />
                        </NFormItem>
                        <div class="buttonCont">
                            <NButton size="large" secondary attr-type="submit" :loading="inProgress">Send a temporary password</NButton>
                        </div>
                    </NForm>
                    <NuxtLink :to="{ name: 'members-login' }" class="back">Back</NuxtLink>
                </div>
            </div>
            <div class="cover">
                <Image :image="linesImage" :width="900" :height="1100" imageClass="cover" />
            </div>
        </div>
        <WebsiteFooter />
    </main>
</template>

<script lang="ts" setup>
import type { FormInst, FormItemRule } from 'naive-ui';
import { NForm, NFormItem, NInput, useDialog, NButton } from 'naive-ui';

definePageMeta({ middleware: 'logged-user' })

const { validateEmail } = useValidation()
const { requestPasswordReset } = useDirectusAuth()
const dialog = useDialog()

const loginForm = ref<FormInst>()
const formData = reactive({
    email: '',
    reset_url: 'https://www.eic-federation.eu/members/login/reset'
})

const rules = {
    email: {
        required: true,
        validator: (rule: FormItemRule, value: string) => {
            if (value == '') return Error('The email address is required')
            else {
                if (validateEmail(value)) return true
                else return Error('This is not a valid email address')
            }
        }
    },
}

const linesImage = ref({
    id: '89dae3e6-fe71-4b6b-b871-b4502b5aa52f',
    width: 1200,
    height: 1600,
    title: '',
})


const { submit, inProgress, error } = useSubmit(
    () => {
        return requestPasswordReset(formData);
    },
    {
        onSuccess: () => {
            dialog.success({
                class: 'successDialog',
                title: 'Email sent. Please check your inbox to continue resetting the password',
                iconPlacement: 'top',
                bordered: true,
                positiveText: 'OK',
                onPositiveClick: async () => {
                    await navigateTo({ name: 'members-login' })
                }
            })
        },
        onError: () => {
            dialog.error({
                class: 'errorDialog',
                title: 'An error occurred',
                iconPlacement: 'top',
                positiveText: 'OK'
            })
        }
    }
);
const validateForm = (e: Event) => {
    e.preventDefault()
    loginForm.value?.validate((formErrors: any) => {
        if (!formErrors) { submit() }
        return
    })
}

useSeoMeta({
    title: 'Forgot password? | Members Area | EIC Federation',
    robots: 'noindex'
})
</script>

<style lang="scss" scoped>
main {
    padding-top: var(--size-padding-header);

    .contents {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--size-margin-page);

        .form {
            padding-left: var(--size-margin-page);

            .body {
                margin-block: var(--size-gutter-double);
            }

            .form-container {
                border-top: var(--size-thickness) solid var(--color-primary);
                padding-top: var(--size-gutter-default);
                max-width: var(--text-maxwidth-body);
            }

            .back {
                color: var(--color-primary);
                text-decoration: underline;
                text-underline-offset: 2.5px;
                text-decoration-thickness: 1px;
            }
        }

        .cover {
            position: relative;
            padding-right: var(--size-margin-page);
            height: 100%;
            min-height: calc(100vh - var(--size-padding-header));
            background-color: #F8F8F6;
        }

        .buttonCont {
            padding: var(--size-gutter-default) 0;
        }
    }


}

@media screen and (max-width: 768px) {
    main {
        .contents {
            display: flex;
            flex-direction: column;
            gap: var(--size-gutter-sections);

            margin-bottom: calc(0px - var(--size-gutter-sections));

            .cover {
                height: 600px;
                min-height: 0;
            }
        }
    }
}
</style>
